import React, { type ReactNode, useMemo } from "react";
import { Alert } from "reactstrap";
import { Loading } from "src/shared/components/Loading";
import {
  ExceptionFormatter,
  type IExceptionFormatterProps,
} from "src/shared/formatters/ExceptionFormatter";

export const useCombinedQueryResult = (
  ...args: Omit<IReactQueryResult, "data" | "children">[]
) => {
  return useMemo(() => {
    return args.reduce<Omit<IReactQueryResult, "data" | "children">>(
      (current, arg) => {
        return {
          error: current.error ?? arg.error,
          isError: current.isError || arg.isError,
          isStale: current.isStale || arg.isStale,
          isLoading: current.isLoading || arg.isLoading,
        };
      },
      args[0],
    );
  }, [args]);
};

export interface IReactQueryResult<TData = unknown> {
  error: IExceptionFormatterProps["exception"];
  isError: boolean;
  isStale?: boolean;
  isLoading: boolean;
  data?: TData;
  children?: ReactNode | ((result: TData) => ReactNode);
  showLoadingOnStale?: boolean;
}

export const ReactQueryResult = <TData,>({
  isError,
  error,
  isLoading,
  isStale,
  data,
  children,
  showLoadingOnStale,
}: IReactQueryResult<TData>) => {
  if (isError) {
    return (
      <Alert color="danger" className="text-left">
        <span className="fas fa-exclamation-circle" />{" "}
        <ExceptionFormatter exception={error} />
      </Alert>
    );
  }

  if (showLoadingOnStale && isStale) {
    return <Loading />;
  }

  if (data === undefined && isLoading) {
    return <Loading />;
  }

  if (children && data !== undefined) {
    if (typeof children === "function") {
      return <>{children(data)}</>;
    } else {
      return <>{children}</>;
    }
  }

  return null;
};
